import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

const IDLE_MINUTES_OPTIONS = [5, 10, 15, 20, 30];

export default function AgentIdlePanel() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const { displayToast } = useToastAlert();

  const [enableAutoIdle, setEnableAutoIdle] = useState(subscription?.enableAutoIdle ?? true);
  const [autoIdleMinutes, setAutoIdleMinutes] = useState(subscription?.autoIdleMinutes ?? 10);
  const [autoIdleStatus, setAutoIdleStatus] = useState(subscription?.autoIdleStatus ?? "Away");
  const [allowOverride, setAllowOverride] = useState(subscription?.allowAgentIdleOverride ?? false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (subscription) {
      setEnableAutoIdle(subscription.enableAutoIdle ?? true);
      setAutoIdleMinutes(subscription.autoIdleMinutes ?? 10);
      setAutoIdleStatus(subscription.autoIdleStatus ?? "Away");
      setAllowOverride(subscription.allowAgentIdleOverride ?? false);
    }
  }, [subscription]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const resp = await api.messaging.post("admin/idle-settings", {
        enableAutoIdle,
        autoIdleMinutes,
        autoIdleStatus,
        allowAgentIdleOverride: allowOverride,
      });
      if (resp.ok) {
        displayToast(Severity.Success, "Idle settings saved.");
        setDirty(false);
      } else {
        displayToast(Severity.Error, "Failed to save idle settings.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to save idle settings.");
    } finally {
      setSaving(false);
    }
  }, [api.messaging, enableAutoIdle, autoIdleMinutes, autoIdleStatus, allowOverride, displayToast]);

  function markDirty() {
    setDirty(true);
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-gray-800">Agent Idle Detection</h2>
        <p className="text-sm text-slate-gray-500 mt-1">
          Automatically set agents to Away when they are inactive. Idle agents stop receiving
          new conversation assignments until they manually return to Available.
        </p>
      </div>

      {/* Enable toggle */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-gray-800">Enable Auto-Idle</h3>
            <p className="text-sm text-slate-gray-500 mt-0.5">
              When enabled, agents who are inactive for the configured threshold will be
              automatically set to the idle status.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enableAutoIdle}
            onClick={() => { setEnableAutoIdle(!enableAutoIdle); markDirty(); }}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cornflower-blue-500 ${
              enableAutoIdle ? "bg-cornflower-blue-500" : "bg-slate-gray-300"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                enableAutoIdle ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>
      </div>

      {enableAutoIdle && (
        <>
          {/* Inactivity threshold */}
          <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
            <h3 className="font-semibold text-slate-gray-800 mb-1">Inactivity Threshold</h3>
            <p className="text-sm text-slate-gray-500 mb-4">
              How long an agent must be inactive before being automatically set to idle.
              Activity includes mouse movement, typing, scrolling, and clicking.
            </p>
            <div className="flex gap-2">
              {IDLE_MINUTES_OPTIONS.map((minutes) => (
                <button
                  key={minutes}
                  onClick={() => { setAutoIdleMinutes(minutes); markDirty(); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    autoIdleMinutes === minutes
                      ? "bg-cornflower-blue-500 text-white border-cornflower-blue-500"
                      : "bg-white text-slate-gray-700 border-slate-gray-200 hover:border-cornflower-blue-300"
                  }`}
                >
                  {minutes} min
                </button>
              ))}
            </div>
          </div>

          {/* Idle status */}
          <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
            <h3 className="font-semibold text-slate-gray-800 mb-1">Idle Status</h3>
            <p className="text-sm text-slate-gray-500 mb-4">
              The status agents will be set to when they go idle. Routing will stop sending
              new conversations to agents in this status.
            </p>
            <div className="flex gap-2">
              {["Away", "Offline"].map((status) => (
                <button
                  key={status}
                  onClick={() => { setAutoIdleStatus(status); markDirty(); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    autoIdleStatus === status
                      ? "bg-cornflower-blue-500 text-white border-cornflower-blue-500"
                      : "bg-white text-slate-gray-700 border-slate-gray-200 hover:border-cornflower-blue-300"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          {/* Agent override */}
          <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-gray-800">Allow Agent Override</h3>
                <p className="text-sm text-slate-gray-500 mt-0.5">
                  When enabled, individual agents can opt out of auto-idle from their
                  profile menu. Disabled by default to prevent gaming.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={allowOverride}
                onClick={() => { setAllowOverride(!allowOverride); markDirty(); }}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cornflower-blue-500 ${
                  allowOverride ? "bg-cornflower-blue-500" : "bg-slate-gray-300"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    allowOverride ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Info box */}
      <div className="bg-cornflower-blue-50 border border-cornflower-blue-200 rounded-lg p-5">
        <h3 className="font-semibold text-cornflower-blue-800 mb-2">How it works</h3>
        <ul className="text-sm text-cornflower-blue-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">1.</span>
            <span>The system monitors mouse, keyboard, scroll, and click activity in the agent's browser.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">2.</span>
            <span>After the inactivity threshold, agents with "Available" status are automatically set to the idle status.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">3.</span>
            <span>A toast notification tells the agent what happened. They can manually set themselves back to Available.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">4.</span>
            <span>Routing automatically skips idle agents, so customers are routed to available agents only.</span>
          </li>
        </ul>
      </div>

      {/* Save button */}
      {dirty && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-cornflower-blue-500 text-white rounded-lg text-sm font-medium hover:bg-cornflower-blue-600 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cornflower-blue-500"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}
